import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { StorePublicProduct } from '@evi/shared';
import { useAuthStore } from '@/store/auth';
import { putCartSnapshot, deleteCartSnapshot } from '@/lib/api';

/**
 * In-app store cart. Device-local, persisted to localStorage via zustand's
 * persist middleware (same pattern as useMapPrefsStore), so a cart survives
 * navigation within a session and across sessions on this device.
 */
export interface CartItem {
  product_id: string;
  part_number: string;
  name: string;
  /** Product tier, snapshotted so the cart can show the shipping class
   *  (Elite Home / pedestal freight / free) without a catalog lookup.
   *  Optional so carts persisted before this field render gracefully
   *  (they fall back to the free-shipping display until re-added); the
   *  server always classifies from the DB, so the charge is unaffected. */
  tier?: string;
  /** Display-only price snapshot in cents. Checkout re-prices server-side in a
   *  later slice, so this is never trusted as the charge amount. */
  unit_price_cents: number;
  qty: number;
}

interface CartState {
  items: CartItem[];
  add: (product: StorePublicProduct) => void;
  remove: (productId: string) => void;
  setQty: (productId: string, qty: number) => void;
  clear: () => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      add: (product) => {
        const { items } = get();
        const existing = items.find((i) => i.product_id === product.id);
        if (existing) {
          set({ items: items.map((i) => (i.product_id === product.id ? { ...i, qty: i.qty + 1 } : i)) });
          return;
        }
        set({
          items: [
            ...items,
            {
              product_id: product.id,
              part_number: product.part_number,
              name: product.name,
              tier: product.tier,
              unit_price_cents: product.price_cents,
              qty: 1,
            },
          ],
        });
      },
      remove: (productId) => set({ items: get().items.filter((i) => i.product_id !== productId) }),
      setQty: (productId, qty) => {
        if (qty <= 0) {
          set({ items: get().items.filter((i) => i.product_id !== productId) });
          return;
        }
        set({ items: get().items.map((i) => (i.product_id === productId ? { ...i, qty } : i)) });
      },
      clear: () => set({ items: [] }),
    }),
    { name: 'evi-web-cart' },
  ),
);

// Derived selectors. Use as useCartStore(selectItemCount).
export const selectItemCount = (s: CartState): number => s.items.reduce((n, i) => n + i.qty, 0);
export const selectSubtotalCents = (s: CartState): number =>
  s.items.reduce((sum, i) => sum + i.unit_price_cents * i.qty, 0);

// ── Server-side snapshot for the abandonment sweep ─────────────────────
// A signed-in driver's cart is mirrored to the server (debounced,
// fire-and-forget) so the hourly sweep can reach someone who left items
// behind. Guests keep a purely local cart. This single subscription
// covers add / remove / setQty AND clear() (empty items -> DELETE), so the
// post-payment clear() in StoreSuccessPage also drops the snapshot. It
// runs out of band: the store actions stay synchronous set() calls and a
// failed call never surfaces to the cart UI.
const SNAPSHOT_DEBOUNCE_MS = 800;
let snapshotTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleCartSnapshot(items: CartItem[]): void {
  if (snapshotTimer) clearTimeout(snapshotTimer);
  snapshotTimer = setTimeout(() => {
    snapshotTimer = null;
    if (!useAuthStore.getState().isAuthenticated) return;
    if (items.length === 0) {
      void deleteCartSnapshot().catch(() => {});
      return;
    }
    void putCartSnapshot(
      items.map((i) => ({
        product_id: i.product_id,
        part_number: i.part_number,
        name: i.name,
        unit_price_cents: i.unit_price_cents,
        quantity: i.qty,
      })),
    ).catch(() => {});
  }, SNAPSHOT_DEBOUNCE_MS);
}

useCartStore.subscribe((state) => scheduleCartSnapshot(state.items));
